import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import Form from 'carbon-react/lib/components/form';
import Button from 'carbon-react/lib/components/button';
import Icon from 'carbon-react/lib/components/icon';
import Message from 'carbon-react/lib/components/message';
import Typography from 'carbon-react/lib/components/typography';

import { DeleteDialogWizardProps, DialogWizardScreen } from '../shared/dialog-wizard.types';
import { DialogWizardBase } from '../shared/dialog-wizard-base';

import styles from '../shared/dialog-wizard.scss';

const getAccessibleDialogName = (ariaLabel: string | undefined, title: React.ReactNode) =>
  ariaLabel || (typeof title === 'string' ? title : undefined);

/**
 * A dialog wizard for destructive actions.
 * Displays a confirm screen with Cancel/Delete buttons,
 * transitions through loading, success, and error states.
 */
export const DeleteDialogWizard = ({
  ariaLabel,
  isOpen,
  onClose,
  onDelete,
  screen,
  title,
  subtitle,
  deleteDescription,
  thingsToKnow,
  loadingMessage,
  successMessage,
  errorMessage,
  deleteButtonText,
  cancelButtonText,
  closeButtonText,
  modalSize,
  dataTestId = 'delete-dialog-wizard'
}: DeleteDialogWizardProps) => {
  const intl = useIntl();

  const defaultDeleteText = intl.formatMessage({
    id: 'common.delete',
    defaultMessage: 'Delete'
  });

  const defaultCancelText = intl.formatMessage({
    id: 'common.cancel',
    defaultMessage: 'Cancel'
  });

  const defaultErrorTitle = intl.formatMessage({
    id: 'common.something_went_wrong',
    defaultMessage: 'Something went wrong'
  });

  const defaultErrorMessage = intl.formatMessage({
    id: 'predict.create_forecast.try_again',
    defaultMessage: 'Try again'
  });

  const composedTitle = (
    <div style={{ marginBottom: 20 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '4px' }}>
        <Icon type="warning" color="#EF6700" />
        <Typography variant="h1" mb={0}>
          {title}
        </Typography>
      </div>
      {subtitle && (
        <Typography variant="p" fontSize="14px" mb={0} color="var(--colorsUtilityYin065)">
          {subtitle}
        </Typography>
      )}
    </div>
  );

  const confirmScreen = (
    <Form
      onSubmit={() => null}
      leftSideButtons={
        <Button onClick={onClose} buttonType="secondary" data-testid={`${dataTestId}-cancel-button`}>
          {cancelButtonText || defaultCancelText}
        </Button>
      }
      saveButton={
        <Button onClick={onDelete} buttonType="primary" destructive data-testid={`${dataTestId}-delete-button`}>
          {deleteButtonText || defaultDeleteText}
        </Button>
      }
      stickyFooter
    >
      {screen === DialogWizardScreen.ERROR && (
        <div className={styles.errorContainer} data-testid={`${dataTestId}-error-message`} style={{ marginBottom: 24 }}>
          <Message variant="error" open showCloseIcon={false} title={defaultErrorTitle}>
            {errorMessage || defaultErrorMessage}
          </Message>
        </div>
      )}
      {deleteDescription && (
        <Typography fontWeight="500" fontSize="14px">
          {deleteDescription}
        </Typography>
      )}
      {thingsToKnow && thingsToKnow.length > 0 && (
        <div className={styles.thingsToKnow} data-testid={`${dataTestId}-things-to-know`}>
          <Typography fontWeight="500" fontSize="14px" mb={0}>
            <FormattedMessage id="common.things_to_know" defaultMessage="Things to know" />
            {':'}
          </Typography>
          <ul className={styles.thingsToKnowList}>
            {thingsToKnow.map((item, index) => (
              <li key={index}>{item}</li>
            ))}
          </ul>
        </div>
      )}
    </Form>
  );

  return (
    <DialogWizardBase
      ariaLabel={getAccessibleDialogName(ariaLabel, title)}
      isOpen={isOpen}
      onClose={onClose}
      screen={screen}
      title={composedTitle}
      loadingMessage={loadingMessage}
      successMessage={successMessage}
      closeButtonText={closeButtonText}
      modalSize={modalSize}
      dataTestId={dataTestId}
    >
      {confirmScreen}
    </DialogWizardBase>
  );
};
